import type { IHelpdeskTicketSummary } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaPanelAccordion from "../../ui-kit/kuika-panel-accordion/kuika-panel-accordion";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import {
  formatTicketDate,
  groupOf,
  groupTickets,
  HelpdeskTicketGroup,
  IHelpdeskTicketGroups
} from "./helpdesk-tickets";

export interface HelpdeskTicketListProps {
  tickets: IHelpdeskTicketSummary[];
  searchText: string;
  selectedTicketNumber: number | null;
  onSelect: (ticketNumber: number) => void;
  /** Tickets the ITSM has beyond `tickets`; "Load more" is offered while this is above 0. */
  remainingCount: number;
  loadingMore: boolean;
  /** Loads the next page and returns its tickets, so the list can open the sections they land in. */
  onLoadMore: () => Promise<IHelpdeskTicketSummary[]>;
}

interface IGroupSection {
  key: HelpdeskTicketGroup;
  /** Also the accordion testid prefix: `${label}DropdownButton` (kept from the Zendesk list). */
  label: string;
}

const SECTIONS: IGroupSection[] = [
  { key: "open", label: "Open" },
  { key: "pending", label: "Pending" },
  { key: "solved", label: "Solved" }
];

const HelpdeskTicketList = ({
  tickets,
  searchText,
  selectedTicketNumber,
  onSelect,
  remainingCount,
  loadingMore,
  onLoadMore
}: HelpdeskTicketListProps): React.ReactElement => {
  const [openSections, setOpenSections] = useState<Record<HelpdeskTicketGroup, boolean>>({
    open: true,
    pending: true,
    solved: false
  });
  const isSearching: boolean = searchText.trim().length > 0;
  const groups: IHelpdeskTicketGroups = useMemo(
    (): IHelpdeskTicketGroups => groupTickets(tickets, searchText),
    [tickets, searchText]
  );

  // Older pages are mostly solved tickets, and Solved starts collapsed: open every section the new
  // tickets land in, or "Load more" would look like it did nothing.
  const loadMore = async (): Promise<void> => {
    const loaded: IHelpdeskTicketSummary[] = await onLoadMore();
    if (loaded.length === 0) return;
    setOpenSections((current: Record<HelpdeskTicketGroup, boolean>): Record<HelpdeskTicketGroup, boolean> => {
      const next: Record<HelpdeskTicketGroup, boolean> = { ...current };
      for (const ticket of loaded) next[groupOf(ticket.zendeskStatus)] = true;
      return next;
    });
  };

  if (tickets.length === 0) {
    return (
      <div className="kuika-helpdesk-list__empty">
        <KuikaSmallText>You don't have any tickets</KuikaSmallText>
      </div>
    );
  }

  return (
    <div className="kuika-helpdesk-list">
      {SECTIONS.filter((section: IGroupSection): boolean => groups[section.key].length > 0).map(
        (section: IGroupSection): React.ReactElement => (
          <KuikaPanelAccordion
            key={section.key}
            label={section.label}
            isOpen={openSections[section.key] || isSearching}
            onChange={(isOpen: boolean): void =>
              setOpenSections(
                (current: Record<HelpdeskTicketGroup, boolean>): Record<HelpdeskTicketGroup, boolean> => ({
                  ...current,
                  [section.key]: isOpen
                })
              )
            }
          >
            <ul className="kuika-helpdesk-list__items">
              {groups[section.key].map((ticket: IHelpdeskTicketSummary): React.ReactElement => (
                <li key={ticket.ticketNumber}>
                  <button
                    type="button"
                    data-testid="ticketItem"
                    className={classNames("kuika-helpdesk-list__item", {
                      "kuika-helpdesk-list__item--selected": ticket.ticketNumber === selectedTicketNumber
                    })}
                    aria-current={ticket.ticketNumber === selectedTicketNumber}
                    onClick={(): void => onSelect(ticket.ticketNumber)}
                  >
                    <span className="kuika-helpdesk-list__subject">{ticket.subject}</span>
                    <span className="kuika-helpdesk-list__meta">
                      <span>{ticket.referenceCode}</span>
                      <span>{formatTicketDate(ticket.updatedAt)}</span>
                    </span>
                    {ticket.hasUnreadReply && <span className="kuika-helpdesk-list__unread" aria-label="New reply" />}
                  </button>
                </li>
              ))}
            </ul>
          </KuikaPanelAccordion>
        )
      )}
      {remainingCount > 0 && (
        <div className="kuika-helpdesk-list__more">
          <KuikaButton
            data-testid="helpdeskLoadMoreButton"
            type="text"
            size="sm"
            color="neutral"
            loading={loadingMore}
            onClick={loadMore}
          >
            Load more ({remainingCount})
          </KuikaButton>
        </div>
      )}
    </div>
  );
};

export default HelpdeskTicketList;
